import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Smartphone, AlertCircle } from 'lucide-react';
import { useApi } from '../../utils/useApi';

interface WhatsappSettingsProps {
    gymId: string;
}

export const WhatsappSettings = ({ gymId }: WhatsappSettingsProps) => {
    const { fetchWithAuth } = useApi();
    const queryClient = useQueryClient();

    const { data: settingsData, isLoading } = useQuery({
        queryKey: ['whatsapp-settings', gymId],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/gyms/${gymId}/whatsapp-settings`);
            return response.data;
        },
        enabled: !!gymId
    });

    const saveMutation = useMutation({
        mutationFn: async (data: any) => {
            return await fetchWithAuth(`/api/gyms/${gymId}/whatsapp-settings`, {
                method: 'POST',
                body: JSON.stringify(data)
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['whatsapp-settings', gymId] });
            alert('Settings saved successfully');
        },
        onError: () => {
            alert('Failed to save settings');
        }
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const data = {
            phoneNumberId: formData.get('phoneNumberId'),
            whatsappBusinessId: formData.get('whatsappBusinessId'),
            accessToken: formData.get('accessToken'),
            apiVersion: formData.get('apiVersion') || 'v18.0'
        };
        saveMutation.mutate(data);
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500">Loading settings...</div>;

    const settings = settingsData || {};

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm relative">
            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Smartphone className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h3>
                    <p className="text-sm text-gray-600 max-w-md">
                        Meta WhatsApp API Configuration will be available soon. We're working on making this feature available to all users.
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3 mb-6 opacity-50">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <Smartphone className="h-6 w-6" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-gray-900">Meta WhatsApp API Configuration</h3>
                    <p className="text-sm text-gray-500">Configure your direct Meta WhatsApp Business API credentials.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" style={{ pointerEvents: 'none', opacity: 0.5 }}>
                <div className="grid grid-cols-1 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID</label>
                        <input
                            name="phoneNumberId"
                            defaultValue={settings.phoneNumberId || ''}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="e.g. 106584739201847"
                            required
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Found in Meta App Dashboard under WhatsApp &gt; Getting Started</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Business Account ID</label>
                        <input
                            name="whatsappBusinessId"
                            defaultValue={settings.whatsappBusinessId || ''}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="e.g. 109283746501928"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Permanent Access Token</label>
                        <input
                            name="accessToken"
                            type="password"
                            defaultValue={settings.accessToken || ''}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="EAAG..."
                            required
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Use a System User access token for permanent access.</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">API Version</label>
                        <input
                            name="apiVersion"
                            defaultValue={settings.apiVersion || 'v18.0'}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            placeholder="v18.0"
                        />
                    </div>
                </div>

                <div className="pt-4 flex items-center justify-between border-t border-gray-100">
                    <div className="flex items-center gap-2 text-amber-600">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-xs font-medium">Credentials are encrypted and stored securely.</span>
                    </div>
                    <button
                        type="submit"
                        disabled={true}
                        className="px-6 py-2 bg-gray-400 text-white rounded-lg font-medium cursor-not-allowed flex items-center gap-2"
                    >
                        Coming Soon
                    </button>
                </div>
            </form>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <h4 className="text-sm font-bold text-gray-700 mb-2">How to get these credentials?</h4>
                <ol className="text-xs text-gray-600 space-y-2 list-decimal ml-4">
                    <li>Create a Meta Developer App (Business type).</li>
                    <li>Add the "WhatsApp" product to your app.</li>
                    <li>Verify your Business Account and add a phone number.</li>
                    <li>Generate a System User Access Token with <code>whatsapp_business_messaging</code> and <code>whatsapp_business_management</code> permissions.</li>
                </ol>
            </div>
        </div>
    );
};
